import { useCallback, useMemo, useState } from 'react';
import * as XLSX from 'xlsx-js-style';
import { Link } from 'react-router-dom';
import { fetchBatchReport } from '../../api/reports';
import {
  PageHeader,
  PageLoading,
  StatusBanner,
  inputClassName,
  labelClassName,
  panelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
  tableHeadClassName,
} from '../../components/ui';

function todayYmd() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

function formatMoney(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const PAGE_SIZE = 25;

const COLUMN_WIDTHS = {
  'Batch ID': 12,
  'Batch Label': 16,
  'Batch Status': 12,
  'Batch Created': 14,
  'Created By': 16,
  Product: 42,
  SKU: 16,
  'ASIN IN': 14,
  'ASIN COM': 14,
  'Ordered Qty': 12,
  'Extra Qty': 10,
  'Buy Qty': 10,
  'Unit Price': 12,
  'Buy Total': 12,
  'Buy Market': 12,
  'US Order ID': 22,
  'US Tracking': 24,
  Carrier: 14,
  HAWB: 14,
  Type: 14,
  Confirmed: 10,
  'Received At': 14,
  'Amazon Order ID': 22,
  Customer: 20,
  'Order Date': 12,
  'Ship By': 12,
};

const MONEY_HEADERS = new Set(['Unit Price', 'Buy Total']);
const CENTER_HEADERS = new Set([
  'Batch ID',
  'Batch Status',
  'Batch Created',
  'Ordered Qty',
  'Extra Qty',
  'Buy Qty',
  'Confirmed',
  'Received At',
  'Order Date',
  'Ship By',
  'Type',
  'HAWB',
]);
const WRAP_HEADERS = new Set(['Product', 'Customer']);

function cellStyle(kind, align, wrap) {
  const thin = { style: 'thin', color: { rgb: 'C5D4E8' } };
  const border = { top: thin, bottom: thin, left: thin, right: thin };
  if (kind === 'header') {
    return {
      font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11, name: 'Calibri' },
      fill: { patternType: 'solid', fgColor: { rgb: '0F4C81' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border,
    };
  }
  if (kind === 'title') {
    return {
      font: { bold: true, color: { rgb: '0F4C81' }, sz: 16, name: 'Calibri' },
      fill: { patternType: 'solid', fgColor: { rgb: 'F0F6FC' } },
      alignment: { horizontal: 'left', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '0F4C81' } },
        bottom: { style: 'thin', color: { rgb: '0F4C81' } },
        left: { style: 'thin', color: { rgb: '0F4C81' } },
        right: { style: 'thin', color: { rgb: '0F4C81' } },
      },
    };
  }
  if (kind === 'meta') {
    return {
      font: { sz: 10, name: 'Calibri', color: { rgb: '475569' }, italic: true },
      fill: { patternType: 'solid', fgColor: { rgb: 'F8FAFC' } },
      alignment: { horizontal: 'left', vertical: 'center' },
      border,
    };
  }
  return {
    font: { sz: 10, name: 'Calibri', color: { rgb: '0F172A' } },
    fill: { patternType: 'solid', fgColor: { rgb: kind === 'zebra' ? 'E8F1F8' : 'FFFFFF' } },
    alignment: { horizontal: align, vertical: 'center', wrapText: wrap },
    border,
  };
}

export default function BatchReportPage({ token }) {
  const [dateMode, setDateMode] = useState('all');
  const [dateFrom, setDateFrom] = useState(todayYmd());
  const [dateTo, setDateTo] = useState(todayYmd());
  const [status, setStatus] = useState('all');
  const [batchCode, setBatchCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState({ type: 'idle', message: '' });
  const [payload, setPayload] = useState(null);
  const [page, setPage] = useState(1);

  const rows = payload?.rows || [];
  const headers = payload?.headers || [];
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const rangeStart = rows.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, rows.length);

  const canRun = useMemo(() => {
    if (dateMode === 'one_day') return Boolean(dateFrom);
    if (dateMode === 'custom') return Boolean(dateFrom && dateTo);
    return true;
  }, [dateMode, dateFrom, dateTo]);

  const loadReport = useCallback(async () => {
    if (!canRun) {
      setStatusMsg({
        type: 'error',
        message: dateMode === 'one_day' ? 'Select a day.' : 'Select From and To dates.',
      });
      return;
    }

    setLoading(true);
    setStatusMsg({ type: 'idle', message: '' });
    try {
      const result = await fetchBatchReport(token, {
        dateMode,
        dateFrom: dateMode === 'all' ? undefined : dateFrom,
        dateTo:
          dateMode === 'all' ? undefined : dateMode === 'one_day' ? dateFrom : dateTo,
        status,
        batchCode: batchCode.trim() || undefined,
      });
      setPayload(result);
      setPage(1);
      setStatusMsg({
        type: 'success',
        message: `Loaded ${result.total?.toLocaleString('en-IN') || 0} line(s) across ${
          result.batchCount?.toLocaleString('en-IN') || 0
        } batch(es).`,
      });
    } catch (error) {
      setPayload(null);
      setStatusMsg({
        type: 'error',
        message: error.message || 'Unable to load batch report.',
      });
    } finally {
      setLoading(false);
    }
  }, [token, dateMode, dateFrom, dateTo, status, batchCode, canRun]);

  function downloadExcel() {
    const matrix = payload?.excelMatrix;
    if (!matrix?.length) {
      setStatusMsg({ type: 'error', message: 'Load the report before downloading.' });
      return;
    }

    const headerRow = matrix[0];
    const colCount = headerRow.length;
    const title = 'Batch Report';
    const meta = `Generated ${todayYmd()} · ${rows.length} line(s) · ${
      payload?.batchCount || 0
    } batch(es) · Status: ${status}${batchCode.trim() ? ` · Batch: ${batchCode.trim()}` : ''}`;

    const titleRow = Array.from({ length: colCount }, (_, i) => ({
      v: i === 0 ? title : '',
      t: 's',
      s: cellStyle('title', 'left', false),
    }));
    const metaRow = Array.from({ length: colCount }, (_, i) => ({
      v: i === 0 ? meta : '',
      t: 's',
      s: cellStyle('meta', 'left', false),
    }));
    const blankRow = Array.from({ length: colCount }, () => ({
      v: '',
      t: 's',
      s: cellStyle('plain', 'left', false),
    }));

    const dataRows = matrix.map((row, rowIndex) =>
      row.map((value, colIndex) => {
        const header = headerRow[colIndex];
        const isHeader = rowIndex === 0;
        const align = MONEY_HEADERS.has(header)
          ? 'right'
          : CENTER_HEADERS.has(header)
            ? 'center'
            : 'left';
        return {
          v: value == null ? '' : value,
          t: typeof value === 'number' ? 'n' : 's',
          s: cellStyle(
            isHeader ? 'header' : rowIndex % 2 === 0 ? 'zebra' : 'plain',
            isHeader ? 'center' : align,
            isHeader || WRAP_HEADERS.has(header)
          ),
          ...(typeof value === 'number' && MONEY_HEADERS.has(header) ? { z: '#,##0.00' } : {}),
        };
      })
    );

    const styled = [titleRow, metaRow, blankRow, ...dataRows];
    const sheet = XLSX.utils.aoa_to_sheet(styled);
    sheet['!cols'] = headerRow.map((header) => ({
      wch: COLUMN_WIDTHS[header] || Math.max(12, String(header).length + 3),
    }));
    sheet['!rows'] = styled.map((_, rowIndex) => {
      if (rowIndex === 0) return { hpt: 28 };
      if (rowIndex === 1) return { hpt: 20 };
      if (rowIndex === 2) return { hpt: 8 };
      if (rowIndex === 3) return { hpt: 32 };
      return { hpt: 18 };
    });
    sheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
    ];
    const filterStart = 3;
    const lastRow = styled.length - 1;
    sheet['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: filterStart, c: 0 },
        e: { r: lastRow, c: colCount - 1 },
      }),
    };
    sheet['!views'] = [
      {
        state: 'frozen',
        ySplit: 4,
        xSplit: 0,
        topLeftCell: 'A5',
        activePane: 'bottomLeft',
      },
    ];

    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Batch Report');
    const codePart = batchCode.trim() ? `-${batchCode.trim().toUpperCase()}` : '';
    XLSX.writeFile(book, `batch-report${codePart}-${status}-${todayYmd()}.xlsx`);
  }

  return (
    <section className="grid gap-5 min-w-0 w-full">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title="Batch report"
          subtitle="Download procurement batch lines with buy qty, tracking, HAWB, and order detail."
        />
        <Link to="/admin/reports" className={`${secondaryButtonClassName} h-9 shrink-0`}>
          ← Reports
        </Link>
      </div>

      {statusMsg.type !== 'idle' ? (
        <StatusBanner status={statusMsg} setStatus={setStatusMsg} />
      ) : null}

      <div className={`${panelClassName} px-5 py-4`}>
        <div className="grid gap-4 lg:grid-cols-12 lg:items-end">
          <label className="grid gap-1 lg:col-span-2">
            <span className={labelClassName}>Date</span>
            <select
              className={`${inputClassName} h-10`}
              value={dateMode}
              onChange={(e) => setDateMode(e.target.value)}
            >
              <option value="all">All</option>
              <option value="one_day">One day</option>
              <option value="custom">Custom</option>
            </select>
          </label>

          {dateMode === 'one_day' ? (
            <label className="grid gap-1 lg:col-span-2">
              <span className={labelClassName}>Day (batch created)</span>
              <input
                type="date"
                className={`${inputClassName} h-10`}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </label>
          ) : null}

          {dateMode === 'custom' ? (
            <>
              <label className="grid gap-1 lg:col-span-2">
                <span className={labelClassName}>From</span>
                <input
                  type="date"
                  className={`${inputClassName} h-10`}
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </label>
              <label className="grid gap-1 lg:col-span-2">
                <span className={labelClassName}>To</span>
                <input
                  type="date"
                  className={`${inputClassName} h-10`}
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </label>
            </>
          ) : null}

          <label className="grid gap-1 lg:col-span-2">
            <span className={labelClassName}>Batch status</span>
            <select
              className={`${inputClassName} h-10`}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="ordered">Ordered</option>
              <option value="shipped">Shipped</option>
              <option value="received">Received</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>

          <label className="grid gap-1 lg:col-span-2">
            <span className={labelClassName}>Batch ID</span>
            <input
              type="text"
              className={`${inputClassName} h-10`}
              placeholder="BH0018"
              value={batchCode}
              onChange={(e) => setBatchCode(e.target.value)}
            />
          </label>

          <div className="flex flex-wrap gap-2 lg:col-span-2">
            <button
              type="button"
              className={`${primaryButtonClassName} h-10`}
              onClick={loadReport}
              disabled={loading || !canRun}
            >
              {loading ? 'Loading…' : 'Run report'}
            </button>
            <button
              type="button"
              className={`${secondaryButtonClassName} h-10`}
              onClick={downloadExcel}
              disabled={loading || !rows.length}
            >
              Download Excel
            </button>
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Date filter uses batch created date (IST). Confirmed + HAWB lines are treated as fulfilled.
        </p>
      </div>

      {loading && !rows.length ? (
        <PageLoading label="Building batch report…" />
      ) : rows.length ? (
        <div className={`${panelClassName} overflow-hidden`}>
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-3">
            <h3 className="text-sm font-semibold text-slate-900">
              {rows.length.toLocaleString('en-IN')} line{rows.length === 1 ? '' : 's'}
              {payload?.batchCount != null
                ? ` · ${payload.batchCount.toLocaleString('en-IN')} batch${
                    payload.batchCount === 1 ? '' : 'es'
                  }`
                : ''}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr>
                  {[
                    'Batch ID',
                    'Status',
                    'Product',
                    'Buy Qty',
                    'Buy Total',
                    'US Order',
                    'Tracking',
                    'HAWB',
                    'Confirmed',
                    'Amazon Order',
                  ].map((header) => (
                    <th key={header} className={tableHeadClassName}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, index) => (
                  <tr
                    key={`${row.batchCode}-${row.sku}-${row.usOrderId}-${rangeStart + index}`}
                    className="border-t border-slate-100"
                  >
                    <td className={tableCellClassName}>{row.batchCode || row.batchName || '—'}</td>
                    <td className={tableCellClassName}>{row.batchStatus || '—'}</td>
                    <td
                      className={`${tableCellClassName} max-w-[14rem] truncate`}
                      title={row.productTitle || ''}
                    >
                      {row.productTitle || '—'}
                    </td>
                    <td className={tableCellClassName}>{row.buyQty ?? '—'}</td>
                    <td className={tableCellClassName}>{formatMoney(row.buyTotal)}</td>
                    <td className={tableCellClassName}>{row.usOrderId || '—'}</td>
                    <td className={tableCellClassName}>{row.usTrackingId || '—'}</td>
                    <td className={tableCellClassName}>{row.hawb || '—'}</td>
                    <td className={tableCellClassName}>{row.confirmed ? 'Yes' : 'No'}</td>
                    <td className={tableCellClassName}>{row.amazonOrderId || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-3 text-sm">
            <p className="text-slate-600">
              Showing {rangeStart}–{rangeEnd} of {rows.length.toLocaleString('en-IN')} line
              {rows.length === 1 ? '' : 's'}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={secondaryButtonClassName}
                disabled={safePage <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </button>
              <span className="min-w-[7rem] text-center font-medium text-slate-800">
                Page {safePage} of {totalPages}
              </span>
              <button
                type="button"
                className={secondaryButtonClassName}
                disabled={safePage >= totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : payload ? (
        <div className={`${panelClassName} px-5 py-10 text-center text-sm text-slate-500`}>
          No rows for these filters.
        </div>
      ) : null}
    </section>
  );
}
